import { useEffect, useRef, useState } from "react";
import { RadioGroup } from "@headlessui/react";
import { t } from "@/i18n-js/instance";
import { usePlans } from "@/react/components/SettingsApp/Plan/RegularPlan/PlansContext";
import { isCurrent } from "@/react/helpers/plansHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useTailwindSmScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { usePlanPageVariant } from "@/react/hooks/usePlanPageVariant";
import type { Plan } from "@circle-react/types/Plan";
import { BadgeV2 } from "@circle-react-shared/uikit/BadgeV2";
import { Radio } from "@circle-react-shared/uikit/Radio";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { AnnualDiscount } from "../shared/AnnualDiscount";
import { PlanCardHighlights } from "./PlanCardHighlights";

const localeRoot = "pricing_plans.plan_settings.plan_selection.plan_selector";

interface PlanCardProps {
  onClick: () => void;
  plan: Plan;
  selected: boolean;
}

export const PlanCard = ({ plan, selected, onClick }: PlanCardProps) => {
  const isCurrentPlan = isCurrent(plan.plan_action);
  const isSmOrMdScreen = useTailwindSmScreenMediaQuery();
  const { currentPlan } = usePlans();

  const isTrialingThisPlan =
    currentPlan?.plan_trial?.active &&
    currentPlan?.plan_trial?.tier === plan.tier;
  const { isExperimentLayoutEnabled } = usePlanPageVariant();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    if (selected && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [selected]);

  const tierDescription = (
    <Typography.BodySm weight="font-normal">
      {t(`${localeRoot}.tier_descriptions.${plan.tier}`)}
    </Typography.BodySm>
  );

  return (
    <RadioGroup.Option
      key={plan.id}
      value={plan.id}
      className={classNames(
        "border-primary cursor-pointer border-b p-4 last:border-none sm:p-6",
        selected ? "bg-gray-50 dark:bg-gray-900" : "bg-primary",
      )}
      onClick={onClick}
    >
      <div className="flex flex-row justify-between">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-start">
              <Radio value={plan.id} label="" checked={selected} />
            </div>
            <div>
              <div className="flex flex-row items-center gap-1">
                <Typography.LabelLg weight="semibold">
                  {plan.display_name}
                </Typography.LabelLg>
                {isCurrentPlan && (
                  <BadgeV2 label={t(`${localeRoot}.current_plan`)} square />
                )}
                {isTrialingThisPlan && (
                  <BadgeV2
                    label={t(`${localeRoot}.trialing_plan`)}
                    square
                    truncate={false}
                    className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                  />
                )}
              </div>
              {isSmOrMdScreen && (
                <div className="mt-2 hidden sm:block">{tierDescription}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex flex-row items-center gap-1 align-middle">
            <Typography.TitleMd weight="font-bold">
              {plan.display_base_monthly_amount}
            </Typography.TitleMd>
            <Typography.BodyMd
              weight="font-normal"
              color="text-slate-700 dark:text-slate-300"
              size="text-sm"
            >
              {t(`${localeRoot}.monthly_interval`)}
            </Typography.BodyMd>
          </div>
          {isSmOrMdScreen && !isExperimentLayoutEnabled && (
            <div className="hidden sm:block">
              <AnnualDiscount plan={plan} />
            </div>
          )}
        </div>
      </div>
      <div
        ref={contentRef}
        className="overflow-hidden duration-300 ease-in-out"
        style={{ height: `${contentHeight}px` }}
      >
        <div className="px-7 sm:px-0">
          {!isSmOrMdScreen && (
            <div className="mb-4 mt-2 block sm:hidden">
              <div>
                <AnnualDiscount plan={plan} />
              </div>
              <div className="mt-4">{tierDescription}</div>
            </div>
          )}
          <PlanCardHighlights plan={plan} />
        </div>
      </div>
    </RadioGroup.Option>
  );
};
